import React, { useEffect, useState } from 'react';
console.log('🔥 TEST LOG - APP STARTED (top of App.tsx)');
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import * as Font from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
// Force currency formatting update
import { InteractionManager, Platform, View, StatusBar as RNStatusBar } from 'react-native';
import { initMobileAdsInPrepare } from './src/ads/initMobileAds';
import AppLoadingScreen from './src/components/AppLoadingScreen';
import AppNavigator from './src/navigation/AppNavigator';
import { GoalsProvider } from './src/context/GoalsContext';
import { AuthProvider } from './src/context/AuthContext';
import { SettingsProvider } from './src/context/SettingsContext';
import { ThemeProvider, useTheme } from './src/context/ThemeContext';
import HeaderStatusBar from './src/components/HeaderStatusBar';
import KeyboardDismissToolbar from './src/components/KeyboardDismissToolbar';
import { IncomeProvider } from './src/context/IncomeContext';
import { SubscriptionProvider, useSubscription } from './src/context/SubscriptionContext';
import { adService } from './src/services/adService';
import { applyTextInputDefaults } from './src/theme/inputDefaults';

applyTextInputDefaults();

// Keep the splash screen visible while we fetch resources
SplashScreen.preventAutoHideAsync();

console.log('App.tsx: Starting app initialization - currency fix applied');

// Inner component to access SubscriptionContext
const AppContent = () => {
  const { shouldShowAds } = useSubscription();
  const { colors } = useTheme();

  useEffect(() => {
    if (Platform.OS === 'android') {
      RNStatusBar.setBackgroundColor(colors.primary.header);
      RNStatusBar.setBarStyle('light-content');
    }
  }, [colors.primary.header]);

  useEffect(() => {
    if (!shouldShowAds()) return;
    const task = InteractionManager.runAfterInteractions(() => {
      console.log('AdMob: Preloading interstitial ads for non-subscriber');
      adService.loadInterstitialAd();
    });
    return () => task.cancel();
  }, [shouldShowAds]);

  return (
    <View style={{ flex: 1 }}>
      <HeaderStatusBar />
      <AppNavigator />
      <KeyboardDismissToolbar />
    </View>
  );
};

export default function App() {
  const [appIsReady, setAppIsReady] = useState(false);

  useEffect(() => {
    async function prepare() {
      try {
        // Pre-load fonts, make any API calls you need to do here
        await Font.loadAsync({
          // Add any custom fonts here if needed
        });
        
        if (Platform.OS !== 'web') {
          await initMobileAdsInPrepare();
        }
      } catch (e) {
        console.warn('App initialization error:', e);
      } finally {
        setAppIsReady(true);
      }
    }

    prepare();
  }, []);

  useEffect(() => {
    if (!appIsReady) return;

    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const root = document.getElementById('root');
      root
        ?.querySelectorAll('.loading-container, .loading-splash, .loading-spinner')
        .forEach((el) => el.remove());
    }

    const frame = requestAnimationFrame(() => {
      SplashScreen.hideAsync().catch((e) =>
        console.warn('SplashScreen.hideAsync failed:', e)
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [appIsReady]);

  if (!appIsReady) {
    return (
      <ThemeProvider>
        <AppLoadingScreen />
      </ThemeProvider>
    );
  }

  console.log('App.tsx: Rendering App component');
  
  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <ThemeProvider>
        <ThemedAppShell />
      </ThemeProvider>
    </GestureHandlerRootView>
  );
}

function ThemedAppShell() {
  const { colors } = useTheme();

  return (
    <View style={{ flex: 1, backgroundColor: colors.background.default }}>
      <SafeAreaProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <GoalsProvider>
              <IncomeProvider>
                <SettingsProvider>
                  <AppContent />
                </SettingsProvider>
              </IncomeProvider>
            </GoalsProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </SafeAreaProvider>
    </View>
  );
}